/**
 * flushModules.ts — opt-in flush-layout registry for inbox-family modules.
 *
 * I5.2 / closes I5 Item 10. The AppShell wraps every module render in a
 * padded Box plus a page-title <Typography> block. Inbox-family modules
 * carry their own header chrome and want to render flush against the
 * main app sidebar and the global top bar — no wrapper padding, no
 * duplicate page-title above the module's own chrome.
 *
 * The "I am flush" knowledge belongs to the child modules per the I5.2
 * authorization (Entry 9 in refactor/state.md). This file IS that
 * knowledge: it owns the registry of department keys whose modules
 * render flush. AppShell only consults `isFlushDepartment(dept)` — it
 * does not carry a hardcoded list of department names for this
 * decision.
 *
 * To opt a future module into flush layout, add its department key to
 * `FLUSH_DEPARTMENTS` here, not in AppShell.
 */

export const FLUSH_DEPARTMENTS: ReadonlySet<string> = new Set<string>([
  'Inbox',
  'HelpDesk',
  'PersonalInbox',
  // Scorecard rebuild P3/P4 (Entry 92 amendment): the graduated
  // ScorecardsView carries its own header chrome (BoardHeaderRow +
  // SavedViewsBar + quarter stepper), so the legacy AppShell scorecard
  // band (title + person/date filters + visibility/CSV controls) must
  // not stack above it.
  'Scorecards',
  'My Scorecard',
  // AI Mission Control carries its own full-bleed header ("Your AI, in one
  // place.") — without this it fell through to the AppShell title fallback and
  // rendered the raw key + a spurious quarter ("AiMissionControl . Q1 2026").
  'AiMissionControl',
  // Analytics hub (S1-c1): owns its chrome — the carbon inner rail + the
  // paper top bar — so the AppShell band and padding must not stack above.
  'Analytics',
  // The Center Console (CC-A, Design System 2.0): a home page that opens with
  // its own carbon hero ("Good morning, …"), so the shell's "Center Console"
  // band would be a second title above it (D-025, D-026). HubPage pads itself.
  'Dashboard',
  // Lord of the Notes (DS2-7): opens on its own carbon band, so the shell's title would stack above it.
  'Call Intelligence',
]);

export function isFlushDepartment(department: string | undefined | null, reportSubPath?: string | null): boolean {
  if (!department) return false;
  // /reports/scorecards dispatches with department 'Reports' — the
  // scorecard surface is identified by the sub-path there.
  if (department === 'Reports' && reportSubPath === 'scorecards') return true;
  return FLUSH_DEPARTMENTS.has(department);
}
